import {
  AssignmentType,
  EffectiveDateType,
  NumberOrNull,
  StringOrNull,
  TaskType,
  TimeType,
  UserType,
} from '../entity/types';
import { smartNumericCompare } from 'utils/string';
import { IDocumentView, IProjectStore } from '../companies/companiesTypes';
import { IRelatedProviderItem } from '../providers/providerTypes';

export interface IFieldNotification {
  fieldName: string;
  values: string[];
  targetUser: string;
  message: string;
  notifyWithSMS: boolean;
  notifyWhenDraft: boolean;
  key: string;
}

export type TaskStatus =
  | 'complete'
  | 'incomplete'
  | 'notStarted'
  | 'started'
  | 'rejected';

export type TaskAction =
  | 'drafted'
  | 'undrafted'
  | 'archived'
  | 'unArchived'
  | 'deleted' // task is deleted
  | 'parentDeleted' // task is deleted as parent deleted
  | 'restored' // task is restored (now not deleted anymore)
  | 'Archive' // task is archived (for future needs)
  | 'Unarchive';

export type PrintTaskParts =
  | 'fields'
  | 'assignee'
  | 'title'
  | 'followers'
  | 'dueDate'
  | 'weight'
  | 'verifiers'
  | 'taskPublisher'
  | 'subTaskPublisher'
  | 'projectId'
  | 'startDate'
  | 'subTasks'
  | 'signatures'
  | 'subTasksTitle'
  | 'mainProgress'
  | 'secondaryProgress'
  | 'relatedProviderItemId'
  | 'subTasksAndProgress'
  | 'ancestors';

export const HiddenFieldValue = '#HIDDEN#'; // field will be hidden

type ProgressType = 'Assignee' | 'Child' | null;

export type NotifySMS = 'None' | 'ActionRequired';

export type RefType =
  | 'user'
  | 'project'
  | 'task'
  | 'string'
  | 'time'
  | 'document'
  | 'providerItem';

export interface IRef<T> {
  value: string;
  id: number;
  type: T;
}

export type IEventType =
  | 'AddAssignmentEvent'
  | 'AddFollowersEvent'
  | 'CreateTaskEvent'
  | 'RemoveAssignmentEvent'
  | 'RemoveFollowersEvent'
  | 'UpdateAssignmentEvent'
  | 'UpdateFollowerEvent'
  | 'UpdateTaskEvent'
  | 'CommentEvent';
export interface IEvent {
  messages: IRef<RefType>[];
  objectMessages: IRef<RefType>[];
  context: IRef<RefType>;
  actor: IRef<'user'>;
  text: string;
  objectText: string;
  title: string;
  type: IEventType;
}

interface IOption {
  [key: string]: string;
}

export interface IMention {
  id: number;
  event: IEvent;
  sourceUserId?: number;
  targetUserId: number;
  creationDate: number;
  taskId?: number;
  resolved?: boolean;
  directAddress?: boolean;
}

export interface ITemplateReportOptions {
  completedTaskCountMain?: string;
  inProgressTaskCountMain?: string;
  notStartedTaskCountMain?: string;
  averageProgressMain?: string;
  completedTaskCountSecondary?: string;
  inProgressTaskCountSecondary?: string;
  notStartedTaskCountSecondary?: string;
  averageProgressSecondary?: string;
  completedTaskCountVerifier?: string;
  rejectedTaskCountVerifier?: string;
  notStartedTaskCountVerifier?: string;
  mainProgressTitle?: string;
  secondaryProgressTitle?: string;
  verifyTitle?: string;
  completedAssignmentCount?: string;
  inProgressAssignmentCount?: string;
  notStartedAssignmentCount?: string;
  averageAssignmentProgress?: string;
  verifierTitle?: string;
}

export interface IReminderInfo {
  target: UserType;
  offsetM: number;
  timeTarget: TimeType;
  message: string;
}

export interface ITaskProgressType {
  main: ProgressType;
  secondary: ProgressType;
}

export interface ITaskNotificationProperties {
  notifyWithSMS: NotifySMS;
  alarmProgress?: boolean;
  assignmentMessageAll?: string;
  assignmentMessageSelf?: string;
  verifierMessageSelf?: string;
  unAssignmentMessageSelf?: string;
  assignmentMessageProgressDoes?: string;
  assignmentMessageProgressVerifier?: string;
  assignmentMessageProgressDoesByOther?: string;
  assignmentMessageProgressVerifierByOther?: string;
  undraftAllSubTasksNotificationMessage?: string;
  undraftAllSubTasksNotificationMessageToUsers?: string;
  taskIsReadyToPublishNotificationMessage?: string;
  subTasksAreReadyToPublishNotificationMessage?: string;
  notifyPastEvents?: boolean;
  fieldNotifications: IFieldNotification[];
}

export interface IUserTaskRelView {
  userId: number;
  taskId?: number;
  canEdit: boolean;
}

export interface IField {
  name: string;
  title?: string;
  required?: boolean;
  relatedToProgress?: boolean;
  highlight?: boolean;
  showInPrint?: boolean;
  value?: string | null;
  options: IOption | null;
  configs?: string[];
  type?:
    | 'text'
    | 'html'
    | 'multiSelect'
    | 'responseLink'
    | 'analyseLink'
    | 'date'
    | 'dateAndTime';
  autoFillFunc?:
    | 'LastSibling_NotStarted'
    | 'LastSibling_Started'
    | 'LastSibling_Done'
    | 'LastSibling_All'
    | 'AllSibling_NotStarted'
    | 'AllSibling_Started'
    | 'ConvertVoiceToMeetingNote'
    | 'LastSibling_NextNumber'
    | undefined;
}

export interface ITaskDocumentView {
  senderId: number;
  deleted: boolean | undefined;
  taskId: number;
  document: IDocumentView;
  id: number;
  description?: string;
}

export interface IAssignmentHistory {
  id: number;
  assignmentId?: number;
  userId: number;
  updateDate: number | Date;
  actorId?: number;
  taskId?: number;
  status: TaskStatus;
  type: AssignmentType;
  progress: number;
  progressDescription?: string;
  lastEditDate: number;
  state: 'Add' | 'Update' | 'Delete' | 'Exists';
}

export interface IAssignmentView {
  id?: number;
  name?: string;
  userId: number;
  taskId?: number;
  status: TaskStatus;
  type: AssignmentType;
  progress: number;
  progressDescription?: string;
  properties?: {
    roleName?: string;
    roleId?: number;
    title?: string;
    projectRoleId?: number;
    implicit?: boolean;
  };
  taskFieldValues?: { [key: string]: string };
  taskView?: ITask;
}
export type TaskDependencyType =
  | 'NoDependency'
  | 'RequireStart'
  | 'RequireFinish';
export interface IRelatedTask {
  taskId: number;
  properties: {
    dependency?: TaskDependencyType;
  };
  blocksProgress?: boolean;
  taskTitle?: string;
  assignee?: IAssignmentView[];
}

export interface ISingleProgressHistory {
  id: number;
  creationDate: number;
  message: string;
  progress: number;
  progressDescription: string | null;
  taskId: number;
  type: string;
}

export type TProgressType =
  | 'mainProgress'
  | 'secondaryProgress'
  | 'verifyProgress';

export type IProgressHistory = {
  [key in TProgressType]: Array<ISingleProgressHistory>;
};

export interface ITask {
  autoLockOnPublish?: boolean;
  archived: boolean;
  assignee: { [key: number]: IAssignmentView };
  assignmentHistory: Array<IAssignmentHistory>;
  assignmentHistoryList: Array<IAssignmentHistory>;
  assigneeCanEdit: boolean;
  assigneeCompleteTitle?: string;
  assigneeNotStartedTitle?: string;
  assigneeStartedTitle?: string;
  assigneeStateTitle?: string;
  assigneeTitle?: string;
  assignmentProgressTitle?: string;
  attachments: Array<ITaskDocumentView>;
  addCreatorToFollowers?: boolean;
  assigneeDefaultToProjectMembers?: boolean;
  followerDefaultToProjectMembers?: boolean;
  draft?: boolean;
  undraftTaskTitle?: string;
  undraftTaskAndSubtaskTitle?: string;
  chatTitle?: string;
  childProgressTitle?: string;
  conversationId: number;
  creationDate: number;
  creator: number;
  conversationReadToken: StringOrNull;
  conversationSendToken: StringOrNull;
  dateAndTime: boolean;
  dueDateTitle?: string;
  dueDate?: number;
  effectiveDate?: number;
  effectiveDateType: EffectiveDateType;
  fields: Array<IField>;
  followers: { [key: number]: IUserTaskRelView };
  followersTitle?: string;
  followerCanEdit: boolean;
  haveConversation: boolean;
  haveDueDate: boolean;
  haveFollower: boolean;
  haveAssignee: boolean;
  haveStartDate: boolean;
  haveSubTask: boolean;
  preventSubTaskCreationWhenDraft: boolean;
  subTaskTitle?: string;
  haveVerifier: boolean;
  haveTaskPublisher: boolean;
  haveSubTaskPublisher: boolean;
  onlyPublisherCanPublish: boolean;
  allowDirectPublish: boolean;
  allowPublishSubtasks: boolean;
  haveVerifyDate: boolean;
  id: number;
  template: boolean;
  lockMark: boolean;
  lockProps: boolean;
  mainProgressTitle?: string;
  mainProgress?: number;
  maxProgress: number;
  minProgress: number;
  notificationProperties: ITaskNotificationProperties;
  onlyVerifierCanMark: boolean;
  parentId: number;
  parents: { [key: number]: ITask };
  includeParentTaskNames?: Array<number>;
  projectId: number;
  printTemplate: StringOrNull;
  relatedProviderItems: Array<IRelatedProviderItem> | null;
  relatedProviderItemId: { id: NumberOrNull; providerId: NumberOrNull };
  relatedProviderItemTitle?: string;
  reminderInfos: Array<IReminderInfo>;
  secondaryProgress?: number;
  stub: boolean;
  startDate?: number;
  startDateTitle?: string;
  subTasksTitle?: string;
  taskName: string;
  deleteState: 'Deleted' | 'ParentDeleted' | 'deleted' | null;
  taskProgressType: ITaskProgressType;
  templateId: number;
  templateReportOptions?: ITemplateReportOptions;
  title: string;
  textTitle: string;
  type: TaskType;
  updateDate: number;
  useParentAssignees: boolean;
  haveWeight: boolean;
  haveRelatedItem: boolean;
  verifierCanMark: boolean;
  singleInParent: boolean;
  continueOnSubTasks: boolean;
  disallowQuickAdd: boolean;
  // verifier: IAssignmentView | null;
  verifiers: { [key: number]: IAssignmentView };
  taskPublisher: IAssignmentView | null;
  subTaskPublisher: IAssignmentView | null;
  verifierCompleteTitle?: string;
  verifierNotStartedTitle?: string;
  verifierRejectTitle?: string;
  verifierStartedTitle?: string;
  verifierTitle?: string;
  taskPublisherTitle?: string;
  subTaskPublisherTitle?: string;
  readyToPublishTaskTitle?: string;
  readyToPublishSubTaskTitle?: string;
  taskIsReadyToPublish?: boolean;
  subTasksAreReadyToPublish?: boolean;
  verifyDate?: number;
  verifyDateTitle?: string;
  verifyStateTitle?: string;
  weight: number;
  order: number;
  mentions: IMention[];
  viewToken?: string;
  ancestors: number[];
  printTemplateKeys?: string;
  useParentVerifier: boolean;
  requiredFields?: string[];
  haveRelatedTask?: boolean;
  allowDuplicate?: boolean;
  relatedTasks?: IRelatedTask[];
  progressHistory?: IProgressHistory;
  validUpdateDurationInMinutes: number;
  preventReduceProgress: boolean;
}

export interface ITaskUpdateView {
  task: ITask;
  affected: { [key: number]: TaskAction };
}

export interface ITasks {
  data: { [key: number]: ITask };
  dirty: Array<number>;
  isLoading: boolean;
  showDeleted: boolean;
}

export const getAllFieldOptions = (
  task: ITask,
  field: IField,
  tasks: ITasks | undefined,
  project: IProjectStore | undefined
) => {
  const valuesFromTemplate =
    !task.template && tasks
      ? Object.values(
          tasks.data[task.templateId]?.fields?.find(
            (f: IField) => f.name === field.name
          )?.options || {}
        )
      : [];
  const valuesFromProjects =
    !task.template && project
      ? project.templates?.find((it) => it.taskId === task.templateId)
          ?.overrides?.fieldOptions[field.name] || []
      : [];
  return Array.from(
    new Set<string>([
      ...Object.values(field.options || {}),
      ...valuesFromProjects,
      ...valuesFromTemplate,
    ])
  ).sort(smartNumericCompare);
};
